import React, { useMemo } from "react";
import { Modal } from "@mui/material";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import type { ProviderAdditionalStep } from "services/paymentApi.types";
import {
  ADDITIONAL_STEP_MODAL_SUBTITLE,
  ADDITIONAL_STEP_MODAL_TITLE,
  buildAdditionalStepNoticeMarkdown,
  resolveAdditionalStepContent,
} from "../../utils/additionalStepContent";
import * as Styled from "./styles";

const markdownNoticeComponents: Components = {
  p: ({ children }) => <span>{children}</span>,
  a: ({ href, children }) => (
    <Styled.ExternalPortalLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {children}
    </Styled.ExternalPortalLink>
  ),
  strong: ({ children }) => (
    <Styled.MarkdownStrong>{children}</Styled.MarkdownStrong>
  ),
};

interface ProviderAdditionalStepModalProps {
  open: boolean;
  additionalStep: ProviderAdditionalStep | null;
  onClose: () => void;
}

function ProviderAdditionalStepModal({
  open,
  additionalStep,
  onClose,
}: ProviderAdditionalStepModalProps) {
  const content = useMemo(() => {
    if (!additionalStep) {
      return null;
    }
    return resolveAdditionalStepContent(additionalStep);
  }, [additionalStep]);

  const noticeMarkdown = useMemo(() => {
    if (!content) {
      return "";
    }
    return buildAdditionalStepNoticeMarkdown(
      content.linkLabel,
      content.messageSuffix,
    );
  }, [content]);

  if (!content) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition keepMounted>
      <Styled.ModalContainer
        role="dialog"
        aria-labelledby="additional-step-title"
      >
        <Styled.Header>
          <Styled.Title id="additional-step-title">
            {ADDITIONAL_STEP_MODAL_TITLE}
          </Styled.Title>
          <Styled.CloseButton onClick={onClose} aria-label="Fechar">
            <Styled.CloseIcon />
          </Styled.CloseButton>
        </Styled.Header>

        <Styled.Body>
          <Styled.Subtitle>{ADDITIONAL_STEP_MODAL_SUBTITLE}</Styled.Subtitle>
          {noticeMarkdown ? (
            <Styled.NoticeRow>
              <Styled.InfoIcon />
              <Styled.NoticeMarkdown>
                <ReactMarkdown components={markdownNoticeComponents}>
                  {noticeMarkdown}
                </ReactMarkdown>
              </Styled.NoticeMarkdown>
            </Styled.NoticeRow>
          ) : null}
        </Styled.Body>
      </Styled.ModalContainer>
    </Modal>
  );
}

export default ProviderAdditionalStepModal;
